import React, { useState, useEffect } from 'react';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation';
import * as utils from '../common/Utils.js';
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, getDocs, getDoc, updateDoc, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const AdminTools = () => {
  const config = useConfig();
  const { currentUser, isReady } = useAdminGuard();

  const SEASON = config.currentSeason;

  const [dataReady, setDataReady] = useState(false);
  const [allGames, setAllGames] = useState([]);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [allGameStats, setAllGameStats] = useState([]);
  const [gameStats, setGameStats] = useState({});

  const [seasonStatus, setSeasonStatus] = useState('idle');
  const [backfillStatus, setBackfillStatus] = useState('idle');
  const [trimStatus, setTrimStatus] = useState('idle');
  const [recalcStatus, setRecalcStatus] = useState('idle');
  const [recalcProgress, setRecalcProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    if (!isReady) return;
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    const [gamesSnap, teamsSnap, seasonTeamsSnap, playersSnap, gameStatsSnap] = await Promise.all([
      getDocs(collection(db, 'games')),
      getDocs(collection(db, 'teams')),
      getDocs(collection(db, 'seasonTeams')),
      getDocs(collection(db, 'players')),
      getDocs(collection(db, 'gameStats')),
    ]);

    const allGames = gamesSnap.docs.map(d => ({ id: d.id, ...d.data(), date: d.data().date.toDate() }));
    allGames.sort((a, b) => a.date - b.date);
    setAllGames(allGames);
    setGames(allGames.filter(g => g.season == SEASON));

    const teamsData = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const seasonTeamsData = seasonTeamsSnap.docs
      .map(d => ({ seasonTeamID: d.id, ...d.data() }))
      .filter(st => st.season == SEASON);
    const mergedTeams = seasonTeamsData
      .map(st => {
        const team = teamsData.find(t => t.id === st.teamID);
        return team ? { ...st, ...team } : null;
      })
      .filter(Boolean);
    setTeams(mergedTeams);

    const allPlayers = playersSnap.docs.map(d => d.data());
    setPlayers(allPlayers);

    const allGameStats = [];
    const gameStatsMap = {};
    gameStatsSnap.forEach(d => {
      const gameID = d.id;
      const stats = (d.data()['stats'] || []).map(s => ({ ...s, gameID }));
      gameStatsMap[gameID] = stats;
      allGameStats.push(...stats);
    });
    setAllGameStats(allGameStats);
    setGameStats(gameStatsMap);

    setDataReady(true);
  }

  async function updateSeasonData() {
    setSeasonStatus('running');
    try {
      const seasonRef = doc(db, 'seasons', SEASON);
      const seasonTeams = utils.getSeasonTeams(games, teams, SEASON);
      const leaders = utils.getSeasonLeaders(allGames, teams, players, allGameStats, SEASON, 10);

      const rawUpcomingGames = utils.getUpcomingGames(games, SEASON);

      // Fetch article data for completed games and merge into each game entry
      const completedGames = rawUpcomingGames.filter(g => g.status === 'completed');
      const articleSnaps = await Promise.all(completedGames.map(g => getDoc(doc(db, 'articles', g.id))));
      const articleMap = {};
      articleSnaps.forEach((snap, i) => {
        if (snap.exists()) {
          const { title, summary, created_at } = snap.data();
          articleMap[completedGames[i].id] = { articleTitle: title, articleSummary: summary, articleCreatedAt: created_at };
        }
      });

      const upcomingGames = rawUpcomingGames.map(g =>
        articleMap[g.id] ? { ...g, ...articleMap[g.id] } : g
      );

      await updateDoc(seasonRef, { teams: seasonTeams, upcomingGames, leaders, updatedAt: serverTimestamp() });

      setSeasonStatus('Done');
    } catch (err) {
      console.error(err);
      setSeasonStatus('Error — check console');
    }
  }

  async function recreateArticlesWithLinks() {
    setBackfillStatus('running');
    try {
      const [articlesSnap, teamsSnap, playersSnap] = await Promise.all([
        getDocs(collection(db, 'articles')),
        getDocs(collection(db, 'teams')),
        getDocs(collection(db, 'players')),
      ]);

      const allTeams = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allPlayers = playersSnap.docs.map(d => d.data());
      const articles = articlesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      let count = 0;
      for (const article of articles) {
        const game = allGames.find(g => g.id === article.game_id);
        if (!game) continue;

        const awayTeam = allTeams.find(t => t.id === game.awayTeamID);
        const homeTeam = allTeams.find(t => t.id === game.homeTeamID);
        if (!awayTeam || !homeTeam) continue;

        const stats = gameStats[game.id] || [];
        const uniquePlayers = [];
        const seenIDs = new Set();
        for (const s of stats.filter(s => s.status === 'played')) {
          if (!seenIDs.has(s.playerID)) {
            seenIDs.add(s.playerID);
            const player = allPlayers.find(p => p.id === s.playerID);
            if (player) uniquePlayers.push({ id: s.playerID, name: utils.displayPlayerName(player) });
          }
        }

        await updateDoc(doc(db, 'articles', article.id), {
          awayTeamID: game.awayTeamID,
          awayTeamName: awayTeam.name,
          homeTeamID: game.homeTeamID,
          homeTeamName: homeTeam.name,
          players: uniquePlayers,
        });

        count++;
      }

      setBackfillStatus(`Done — ${count} article${count !== 1 ? 's' : ''} updated`);
    } catch (err) {
      console.error(err);
      setBackfillStatus('Error — check console');
    }
  }

  async function trimPlayerNames() {
    setTrimStatus('running');
    try {
      const playersSnap = await getDocs(collection(db, 'players'));
      let count = 0;

      for (const d of playersSnap.docs) {
        const player = d.data();
        const trimmedFirst = (player.firstName || '').trim();
        const trimmedLast = (player.lastName || '').trim();

        if (trimmedFirst !== player.firstName || trimmedLast !== player.lastName) {
          await updateDoc(doc(db, 'players', d.id), {
            firstName: trimmedFirst,
            lastName: trimmedLast,
          });
          count++;
        }
      }

      setTrimStatus(`Done — ${count} player${count !== 1 ? 's' : ''} updated`);
    } catch (err) {
      console.error(err);
      setTrimStatus('Error — check console');
    }
  }

  async function recalculatePlayerViews() {
    setRecalcStatus('running');
    try {
      const [baseTeamsSnap, allSeasonTeamsSnap] = await Promise.all([
        getDocs(collection(db, 'teams')),
        getDocs(collection(db, 'seasonTeams')),
      ]);

      const baseTeams = baseTeamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      function getTeamInfo(teamID) {
        const bt = baseTeams.find(t => t.id === teamID);
        return { id: teamID, name: bt?.name || teamID, nameAbbv: bt?.nameAbbv || teamID };
      }

      const playerIDs = [...new Set(allGameStats.map(s => s.playerID))];
      let count = 0;
      setRecalcProgress({ current: 0, total: playerIDs.length });

      for (const playerID of playerIDs) {
        const careerStats = allGameStats.filter(s => s.playerID === playerID);
        const playedStats = careerStats.filter(s => s.status === 'played');
        if (playedStats.length === 0) continue;

        // Career wins/losses
        let wins = 0, losses = 0, regularSeasonWins = 0, regularSeasonLosses = 0;
        let playoffWins = 0, playoffLosses = 0;
        for (const s of playedStats) {
          const game = allGames.find(g => g.id === s.gameID);
          if (!game) continue;
          const isWin = game.winner === s.teamID;
          if (isWin) { wins++; game.type === 'regular_season' ? regularSeasonWins++ : playoffWins++; }
          else { losses++; game.type === 'regular_season' ? regularSeasonLosses++ : playoffLosses++; }
        }

        // Helper: average a stat over an array of played stats
        const avg = (arr, key) => arr.length ? (arr.reduce((sum, s) => sum + (s[key] ?? 0), 0) / arr.length).toFixed(1) : '0';

        // Career averages use played stats only (matching getAverageStatsForPlayer)
        const careerSB = playedStats.filter(s => { const g = allGames.find(g => g.id === s.gameID); return g && g.date > utils.DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED; });
        const careerTeamIDs = [...new Set(careerStats.map(s => s.teamID))]; // all stats for team list

        // Season set derived from ALL stats (so DNP-only seasons still appear)
        const seasonSet = new Set(careerStats.map(s => { const g = allGames.find(g => g.id === s.gameID); return g ? String(g.season) : null; }).filter(Boolean));
        const seasonAverages = {};
        const seasonStats = {};

        for (const season of seasonSet) {
          // ALL stats for this season (used for game list and team list)
          const allSeasonStats = careerStats.filter(s => { const g = allGames.find(g => g.id === s.gameID); return g && String(g.season) === season; });
          // Played-only stats for averages
          const sp = allSeasonStats.filter(s => s.status === 'played');
          const sbSp = sp.filter(s => { const g = allGames.find(g => g.id === s.gameID); return g && g.date > utils.DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED; });
          const teamIDs = [...new Set(allSeasonStats.map(s => s.teamID))]; // all stats for team list

          if (sp.length > 0) {
            seasonAverages[season] = {
              points: avg(sp, 'points'),
              rebounds: avg(sp, 'rebounds'),
              assists: avg(sp, 'assists'),
              steals: avg(sbSp, 'steals'),
              blocks: avg(sbSp, 'blocks'),
              gamesPlayed: sp.length,
              teams: teamIDs.map(getTeamInfo),
            };
          }

          // Game list includes ALL stats (DNP, forfeit, no-stats, played)
          seasonStats[season] = allSeasonStats
            .map(s => {
              const game = allGames.find(g => g.id === s.gameID);
              if (!game) return null;
              const isHome = game.homeTeamID === s.teamID;
              const opponentID = isHome ? game.awayTeamID : game.homeTeamID;
              return {
                assists: s.assists ?? 0,
                blocks: s.blocks ?? 0,
                points: s.points ?? 0,
                rebounds: s.rebounds ?? 0,
                steals: s.steals ?? 0,
                gameID: s.gameID,
                team: getTeamInfo(s.teamID),
                opponent: getTeamInfo(opponentID),
                result: game.winner === s.teamID ? 'W' : 'L',
                score: game.score ? (isHome ? `${game.score.home} - ${game.score.away}` : `${game.score.away} - ${game.score.home}`) : '',
                status: s.status,
                date: game.date,
              };
            })
            .filter(Boolean)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
        }

        seasonAverages['all'] = {
          points: avg(playedStats, 'points'),
          rebounds: avg(playedStats, 'rebounds'),
          assists: avg(playedStats, 'assists'),
          steals: avg(careerSB, 'steals'),
          blocks: avg(careerSB, 'blocks'),
          gamesPlayed: playedStats.length,
          teams: careerTeamIDs.map(getTeamInfo),
        };

        await setDoc(doc(db, 'playerViews', playerID), {
          seasonAverages,
          seasonStats,
          career: { wins, losses, regularSeasonWins, regularSeasonLosses, playoffWins, playoffLosses },
        }, { merge: true });

        count++;
        setRecalcProgress(prev => ({ ...prev, current: prev.current + 1 }));
      }

      setRecalcStatus(`Done — ${count} player${count !== 1 ? 's' : ''} updated`);
    } catch (err) {
      console.error(err);
      setRecalcStatus('Error — check console');
    }
  }

  if (!dataReady || currentUser == null) {
    return (
      <div className="Account">
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <div>
      <div className="league-content">
        <div className="league-body">
          <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
          <p className="league-title-standard-secondary text-align-center">Tools</p>

          <div className="league-table-container schedule medium-item-buttons">
            <button
              className="btn btn-dark"
              type="button"
              onClick={updateSeasonData}
              disabled={seasonStatus === 'running'}
            >
              Update Season Data
            </button>
            {seasonStatus !== 'idle' && (
              <p style={{ marginTop: 8, color: seasonStatus.startsWith('Error') ? 'red' : '#555' }}>
                {seasonStatus}
              </p>
            )}
          </div>

          <div className="league-table-container schedule medium-item-buttons">
            <button
              className="btn btn-dark"
              type="button"
              onClick={recreateArticlesWithLinks}
              disabled={backfillStatus === 'running'}
            >
              Recreate Articles with Links
            </button>
            {backfillStatus !== 'idle' && (
              <p style={{ marginTop: 8, color: backfillStatus.startsWith('Error') ? 'red' : '#555' }}>
                {backfillStatus}
              </p>
            )}
          </div>

          <div className="league-table-container schedule medium-item-buttons">
            <button
              className="btn btn-dark"
              type="button"
              onClick={trimPlayerNames}
              disabled={trimStatus === 'running'}
            >
              Trim Player Name Whitespace
            </button>
            {trimStatus !== 'idle' && (
              <p style={{ marginTop: 8, color: trimStatus.startsWith('Error') ? 'red' : '#555' }}>
                {trimStatus}
              </p>
            )}
          </div>

          <div className="league-table-container schedule medium-item-buttons">
            <button
              className="btn btn-dark"
              type="button"
              onClick={recalculatePlayerViews}
              disabled={recalcStatus === 'running'}
            >
              Recalculate Player Views
            </button>
            {recalcStatus === 'running' && recalcProgress.total > 0 && (
              <div style={{ marginTop: 10, width: '100%' }}>
                <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  {recalcProgress.current} / {recalcProgress.total} players
                </p>
                <div style={{ background: '#e0e0e0', borderRadius: 4, height: 8, width: '100%' }}>
                  <div style={{
                    background: '#333',
                    borderRadius: 4,
                    height: 8,
                    width: `${(recalcProgress.current / recalcProgress.total) * 100}%`,
                    transition: 'width 0.2s ease',
                  }} />
                </div>
              </div>
            )}
            {recalcStatus !== 'idle' && recalcStatus !== 'running' && (
              <p style={{ marginTop: 8, color: recalcStatus.startsWith('Error') ? 'red' : '#555' }}>
                {recalcStatus}
              </p>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminTools;
